/**
 * Program Detail Page - Complete Program Management Dashboard
 * Copying best practices from Stripe Dashboard + Notion Workspace
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeft, Users, Settings, Calendar, BarChart3, 
  TrendingUp, Award, Target, FileText, Edit, Save,
  RefreshCw, UserPlus, History, Clock, CheckCircle,
  AlertCircle, Download, Eye, Trash2, Plus
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useTheme } from '@/contexts/ThemeContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ProgramDetailPage = ({ user }) => {
  const { programId } = useParams();
  const navigate = useNavigate();
  const { colors } = useTheme();
  
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Overview tab data
  const [stats, setStats] = useState(null);
  const [recentActivity, setRecentActivity] = useState([]);
  
  // Users tab data
  const [programUsers, setProgramUsers] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  
  // Settings tab data
  const [editMode, setEditMode] = useState(false);
  const [programSettings, setProgramSettings] = useState({});
  
  // Seasons tab data
  const [seasons, setSeasons] = useState([]);
  
  useEffect(() => {
    if (programId) {
      fetchProgramData();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId]);
  
  useEffect(() => {
    if (activeTab === 'users' && program) {
      fetchProgramUsers();
    } else if (activeTab === 'seasons' && program) {
      fetchSeasons();
    } else if (activeTab === 'analytics' && program) {
      fetchAnalytics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, program]);
  
  const fetchProgramData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${programId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProgram(data);
        setProgramSettings({
          name: data.name,
          display_name: data.display_name || data.name,
          city: data.city,
          state: data.state,
          status: data.status
        });
        
        // Fetch overview stats
        await fetchOverviewStats(data.id);
      } else {
        toast.error('Failed to load program');
        navigate('/platform');
      }
    } catch (error) {
      console.error('Error fetching program:', error);
      toast.error('Error loading program');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchOverviewStats = async (progId) => {
    try {
      const token = localStorage.getItem('token');
      const [usersRes, goalsRes, winsRes] = await Promise.all([
        fetch(`${API_URL}/api/platform/programs/${progId}/users`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/goals?program_id=${progId}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        fetch(`${API_URL}/api/wins?program_id=${progId}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      const users = usersRes.ok ? await usersRes.json() : { users: [] };
      const goals = goalsRes.ok ? await goalsRes.json() : [];
      const wins = winsRes.ok ? await winsRes.json() : [];
      
      setStats({
        total_users: users.users?.length || 0,
        total_youth: users.users?.filter(u => u.role === 'youth').length || 0,
        total_staff: users.users?.filter(u => ['coach', 'lead_monitor', 'program_admin'].includes(u.role)).length || 0,
        total_goals: goals.length || 0,
        total_wins: wins.length || 0,
        active_youth: users.users?.filter(u => u.role === 'youth' && u.status === 'active').length || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const fetchProgramUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${programId}/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProgramUsers(data.users || []);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };
  
  const fetchSeasons = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${programId}/seasons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setSeasons(data.seasons || []);
      }
    } catch (error) {
      console.error('Error fetching seasons:', error);
    }
  };
  
  const fetchAnalytics = async () => {
    // TODO: Implement analytics fetching
  };
  
  const updateProgramSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/platform/programs/${programId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(programSettings)
      });
      
      if (response.ok) {
        toast.success('Program settings updated!');
        setEditMode(false);
        fetchProgramData();
      } else {
        toast.error('Failed to update settings');
      }
    } catch (error) {
      toast.error('Error updating settings');
    }
  };
  
  const filteredUsers = selectedRole 
    ? programUsers.filter(u => u.role === selectedRole)
    : programUsers;
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <div className="animate-spin w-12 h-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!program) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: colors.background }}>
        <Card className="p-8 text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
          <h2 className="text-xl font-bold mb-2">Program Not Found</h2>
          <Button onClick={() => navigate('/platform')}>Back to Dashboard</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.background }}>
      {/* Header */}
      <div 
        className="border-b sticky top-0 z-50 backdrop-blur-md"
        style={{
          backgroundColor: colors.header,
          borderColor: colors.border
        }}
      >
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate('/platform')}
                style={{ color: colors.text_on_primary }}
                className="hover:bg-white/10"
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-2xl font-bold" style={{ color: colors.text_on_primary }}>
                  {program.display_name || program.branding?.display_name || program.name}
                </h1>
                <p className="text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                  {program.city}, {program.state} · {program.slug}
                </p>
              </div>
            </div>
            
            <Badge className={program.status === 'active' ? 'bg-green-500/20 text-green-300' : 'bg-yellow-500/20 text-yellow-300'}>
              {program.status}
            </Badge>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList 
            className="border mb-6"
            style={{
              backgroundColor: colors.card,
              borderColor: colors.border
            }}
          >
            <TabsTrigger value="overview" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <BarChart3 className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <Users className="w-4 h-4 mr-2" />
              Users
            </TabsTrigger>
            <TabsTrigger value="seasons" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <Calendar className="w-4 h-4 mr-2" />
              Seasons
            </TabsTrigger>
            <TabsTrigger value="settings" className="data-[state=active]:bg-blue-600 data-[state=active]:text-white" style={{ color: colors.text }}>
              <Settings className="w-4 h-4 mr-2" />
              Settings
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="space-y-6">
              {/* Key Metrics */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(34,197,94,0.1)' }}>
                      <Users className="w-5 h-5" style={{ color: '#22C55E' }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: colors.text }}>{stats?.total_youth || 0}</p>
                      <p className="text-xs" style={{ color: colors.text_muted }}>Youth</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(59,130,246,0.1)' }}>
                      <Users className="w-5 h-5" style={{ color: '#3B82F6' }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: colors.text }}>{stats?.total_staff || 0}</p>
                      <p className="text-xs" style={{ color: colors.text_muted }}>Staff</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(168,85,247,0.1)' }}>
                      <Target className="w-5 h-5" style={{ color: '#A855F7' }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: colors.text }}>{stats?.total_goals || 0}</p>
                      <p className="text-xs" style={{ color: colors.text_muted }}>Goals</p>
                    </div>
                  </div>
                </Card>
                
                <Card className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'rgba(249,115,22,0.1)' }}>
                      <Award className="w-5 h-5" style={{ color: '#F97316' }} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold" style={{ color: colors.text }}>{stats?.total_wins || 0}</p>
                      <p className="text-xs" style={{ color: colors.text_muted }}>Wins</p>
                    </div>
                  </div>
                </Card>
              </div>

              {/* Program Info Card */}
              <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <h3 className="text-lg font-bold mb-4" style={{ color: colors.text }}>Program Information</h3>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text_muted }}>Organization</p>
                    <p className="font-medium" style={{ color: colors.text }}>{program.organization_name || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text_muted }}>Location</p>
                    <p className="font-medium" style={{ color: colors.text }}>{program.city}, {program.state}</p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text_muted }}>Current Season</p>
                    <p className="font-medium" style={{ color: colors.text }}>
                      {program.seasons?.[program.seasons.length - 1]?.season_name || 'No active season'}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs mb-1" style={{ color: colors.text_muted }}>Status</p>
                    <Badge className={program.status === 'active' ? 'bg-green-500/20 text-green-600' : 'bg-gray-500/20 text-gray-600'}>
                      {program.status}
                    </Badge>
                  </div>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>Program Users</h2>
                <Button className="bg-green-600 hover:bg-green-700 text-white">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Invite User
                </Button>
              </div>

              {/* Role Filter Cards */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                {['youth', 'coach', 'lead_monitor', 'program_admin', 'parent'].map(role => {
                  const count = programUsers.filter(u => u.role === role).length;
                  return (
                    <Card
                      key={role}
                      className={`p-4 text-center cursor-pointer transition-all hover:shadow-md ${
                        selectedRole === role ? 'ring-2 ring-blue-500' : ''
                      }`}
                      style={{
                        backgroundColor: selectedRole === role ? 'rgba(59,130,246,0.1)' : colors.card,
                        borderColor: selectedRole === role ? '#3B82F6' : colors.border
                      }}
                      onClick={() => setSelectedRole(selectedRole === role ? null : role)}
                    >
                      <p className="text-xl font-bold" style={{ color: colors.text }}>{count}</p>
                      <p className="text-xs capitalize" style={{ color: colors.text_muted }}>{role.replace('_', ' ')}</p>
                    </Card>
                  );
                })}
              </div>

              {/* User List */}
              <Card style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead style={{ backgroundColor: colors.surface }}>
                      <tr>
                        <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Name</th>
                        <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Email</th>
                        <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Role</th>
                        <th className="text-left p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Points</th>
                        <th className="text-right p-4 text-sm font-medium" style={{ color: colors.text_secondary }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredUsers.map(u => (
                        <tr key={u.id} style={{ borderTop: `1px solid ${colors.border}` }}>
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <span className="text-lg">{u.avatar_emoji || '😊'}</span>
                              <span className="font-medium" style={{ color: colors.text }}>{u.name}</span>
                            </div>
                          </td>
                          <td className="p-4" style={{ color: colors.text }}>{u.email}</td>
                          <td className="p-4">
                            <Badge className="bg-blue-500/10 text-blue-600 border-0">
                              {u.role.replace('_', ' ')}
                            </Badge>
                          </td>
                          <td className="p-4 font-medium" style={{ color: colors.text }}>{u.total_points || 0}</td>
                          <td className="p-4 text-right">
                            <Button variant="ghost" size="sm" className="text-blue-600 hover:bg-blue-50">
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card>
            </div>
          </TabsContent>

          {/* Seasons Tab */}
          <TabsContent value="seasons">
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>Program Seasons</h2>
                <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  New Season
                </Button>
              </div>

              <div className="space-y-3">
                {seasons.length === 0 ? (
                  <Card className="p-12 text-center" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                    <Calendar className="w-16 h-16 mx-auto mb-4" style={{ color: colors.text_muted }} />
                    <h3 className="text-lg font-bold mb-2" style={{ color: colors.text }}>No Seasons Yet</h3>
                    <p style={{ color: colors.text_secondary }}>Create a season to organize program cycles.</p>
                  </Card>
                ) : (
                  seasons.map(season => (
                    <Card key={season.id} className="p-5" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-bold text-lg" style={{ color: colors.text }}>{season.season_name} {season.year}</h3>
                          <p className="text-sm" style={{ color: colors.text_secondary }}>
                            {season.start_date} - {season.end_date}
                          </p>
                        </div>
                        <Badge className={season.is_current ? 'bg-green-500/20 text-green-600' : 'bg-gray-500/20 text-gray-600'}>
                          {season.is_current ? 'Current' : 'Past'}
                        </Badge>
                      </div>
                    </Card>
                  ))
                )}
              </div>
            </div>
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card className="p-6" style={{ backgroundColor: colors.card, borderColor: colors.border }}>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold" style={{ color: colors.text }}>Program Settings</h2>
                {!editMode ? (
                  <Button onClick={() => setEditMode(true)} variant="outline" style={{ borderColor: colors.border, color: colors.text }}>
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                ) : (
                  <div className="flex gap-2">
                    <Button onClick={() => setEditMode(false)} variant="outline">
                      Cancel
                    </Button>
                    <Button onClick={updateProgramSettings} className="bg-blue-600 hover:bg-blue-700 text-white">
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </Button>
                  </div>
                )}
              </div>

              <div className="space-y-4">
                <div>
                  <Label style={{ color: colors.text }}>Program Name</Label>
                  <Input
                    value={programSettings.name || ''}
                    onChange={(e) => setProgramSettings({...programSettings, name: e.target.value})}
                    disabled={!editMode}
                    style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                  />
                </div>
                
                <div>
                  <Label style={{ color: colors.text }}>Display Name</Label>
                  <Input
                    value={programSettings.display_name || ''}
                    onChange={(e) => setProgramSettings({...programSettings, display_name: e.target.value})}
                    disabled={!editMode}
                    style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label style={{ color: colors.text }}>City</Label>
                    <Input
                      value={programSettings.city || ''}
                      onChange={(e) => setProgramSettings({...programSettings, city: e.target.value})}
                      disabled={!editMode}
                      style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                    />
                  </div>
                  <div>
                    <Label style={{ color: colors.text }}>State</Label>
                    <Input
                      value={programSettings.state || ''}
                      onChange={(e) => setProgramSettings({...programSettings, state: e.target.value})}
                      disabled={!editMode}
                      style={{ backgroundColor: colors.surface, borderColor: colors.border, color: colors.text }}
                    />
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default ProgramDetailPage;
